import React, { useState } from "react";
import { Activity, CalendarClock, FlaskConical, Layers, LogOut, Menu, Monitor, Moon, Radio, Sun, TrendingUp, User, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const Navigation = ({ username, onLogout, isAdmin = false, darkMode = false, onToggleDarkMode }) => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isDashboard = location.pathname === "/dashboard" || location.pathname.startsWith("/dashboard/");
  const isBacktesting = location.pathname.startsWith("/backtesting");
  const isDayAhead = location.pathname.startsWith("/dayahead");
  const isComplete = location.pathname.startsWith("/complete");
  const isLive = location.pathname === "/live" || location.pathname.startsWith("/live/");
  const isLiveTrading = location.pathname.startsWith("/live-trading");
  const isHealth = location.pathname.startsWith("/health");

  const linkCls = (active) =>
    `inline-flex items-center h-16 px-1 border-b-2 text-sm font-medium transition-colors ${
      active
        ? darkMode
          ? "border-slate-200 text-slate-100"
          : "border-gray-900 text-gray-900"
        : darkMode
          ? "border-transparent text-slate-400 hover:border-slate-600 hover:text-slate-200"
          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
    }`;

  // Mobile links use a full-width row style rather than the desktop bottom-border tabs.
  const mobileLinkCls = (active) =>
    `flex items-center gap-2 px-2 py-3 rounded-md text-sm font-medium transition-colors ${
      active
        ? darkMode
          ? "bg-slate-800 text-slate-100"
          : "bg-gray-100 text-gray-900"
        : darkMode
          ? "text-slate-300 hover:bg-slate-800 hover:text-slate-100"
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
    }`;

  // Live Trading is the PROD box — rendered red so it never blends in with the
  // paper/dev tabs (user request 2026-08-03).
  const redLinkCls = (active) =>
    `inline-flex items-center h-16 px-1 border-b-2 text-sm font-semibold transition-colors ${
      active
        ? darkMode
          ? "border-rose-400 text-rose-300"
          : "border-rose-600 text-rose-600"
        : darkMode
          ? "border-transparent text-rose-400/80 hover:border-rose-500 hover:text-rose-300"
          : "border-transparent text-rose-600/80 hover:border-rose-400 hover:text-rose-600"
    }`;

  const redMobileLinkCls = (active) =>
    `flex items-center gap-2 px-2 py-3 rounded-md text-sm font-semibold transition-colors ${
      active
        ? darkMode
          ? "bg-rose-500/15 text-rose-300"
          : "bg-rose-50 text-rose-700"
        : darkMode
          ? "text-rose-400/80 hover:bg-rose-500/10 hover:text-rose-300"
          : "text-rose-600/80 hover:bg-rose-50 hover:text-rose-700"
    }`;

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav className={`${darkMode ? "bg-slate-900 shadow-xl shadow-slate-950/30 border-b border-slate-800" : "bg-white shadow-md"}`}>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center gap-8">
            <h1 className={`text-xl font-bold ${darkMode ? "text-slate-100" : "text-gray-800"}`}>
              Puzzle XBID
            </h1>
            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-8">
              {isAdmin && (
                <Link to="/dashboard" className={linkCls(isDashboard)}>
                  <Monitor className="w-4 h-4 mr-2" />
                  Dashboard
                </Link>
              )}
              <Link to="/backtesting" className={linkCls(isBacktesting)}>
                <FlaskConical className="w-4 h-4 mr-2" />
                Backtesting
              </Link>
              <Link to="/dayahead" className={linkCls(isDayAhead)}>
                <CalendarClock className="w-4 h-4 mr-2" />
                Day Ahead Backtesting
              </Link>
              <Link to="/complete" className={linkCls(isComplete)}>
                <Layers className="w-4 h-4 mr-2" />
                Complete Backtesting
              </Link>
              <Link to="/live" className={linkCls(isLive)}>
                <Radio className="w-4 h-4 mr-2" />
                Live Testing
              </Link>
              <Link to="/live-trading" className={redLinkCls(isLiveTrading)}>
                <TrendingUp className="w-4 h-4 mr-2" />
                Live Trading
              </Link>
              {isAdmin && (
                <Link to="/health" className={linkCls(isHealth)}>
                  <Activity className="w-4 h-4 mr-2" />
                  Health
                </Link>
              )}
            </div>
          </div>

          {/* Desktop actions */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              type="button"
              onClick={onToggleDarkMode}
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                darkMode
                  ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
              title="Toggle dark mode"
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              Dark mode
            </button>
            <div className={`flex items-center text-sm ${darkMode ? "text-slate-200" : "text-gray-700"}`}>
              <User className="w-4 h-4 mr-1" />
              <span>{username}</span>
            </div>
            <button
              onClick={onLogout}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Logout
            </button>
          </div>

          {/* Mobile hamburger toggle */}
          <div className="flex md:hidden items-center">
            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="Toggle navigation menu"
              aria-expanded={mobileOpen}
              className={`inline-flex items-center justify-center rounded-md p-2 transition-colors ${
                darkMode
                  ? "text-slate-200 hover:bg-slate-800"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className={`md:hidden border-t ${darkMode ? "border-slate-800" : "border-gray-200"}`}>
          <div className="px-4 py-3 space-y-1">
            {isAdmin && (
              <Link to="/dashboard" onClick={closeMobile} className={mobileLinkCls(isDashboard)}>
                <Monitor className="w-4 h-4" />
                Dashboard
              </Link>
            )}
            <Link to="/backtesting" onClick={closeMobile} className={mobileLinkCls(isBacktesting)}>
              <FlaskConical className="w-4 h-4" />
              Backtesting
            </Link>
            <Link to="/dayahead" onClick={closeMobile} className={mobileLinkCls(isDayAhead)}>
              <CalendarClock className="w-4 h-4" />
              Day Ahead Backtesting
            </Link>
            <Link to="/complete" onClick={closeMobile} className={mobileLinkCls(isComplete)}>
              <Layers className="w-4 h-4" />
              Complete Backtesting
            </Link>
            <Link to="/live" onClick={closeMobile} className={mobileLinkCls(isLive)}>
              <Radio className="w-4 h-4" />
              Live Testing
            </Link>
            <Link to="/live-trading" onClick={closeMobile} className={redMobileLinkCls(isLiveTrading)}>
              <TrendingUp className="w-4 h-4" />
              Live Trading
            </Link>
            {isAdmin && (
              <Link to="/health" onClick={closeMobile} className={mobileLinkCls(isHealth)}>
                <Activity className="w-4 h-4" />
                Health
              </Link>
            )}

            <div className={`my-2 border-t ${darkMode ? "border-slate-800" : "border-gray-200"}`} />

            <div className={`flex items-center gap-2 px-2 py-2 text-sm ${darkMode ? "text-slate-200" : "text-gray-700"}`}>
              <User className="w-4 h-4" />
              <span>{username}</span>
            </div>
            <button
              type="button"
              onClick={() => {
                onToggleDarkMode();
              }}
              className={`w-full flex items-center gap-2 px-2 py-3 rounded-md text-sm font-medium transition-colors ${
                darkMode
                  ? "text-slate-300 hover:bg-slate-800 hover:text-slate-100"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              Dark mode
            </button>
            <button
              onClick={() => {
                closeMobile();
                onLogout();
              }}
              className="w-full inline-flex items-center gap-2 px-2 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
